import type { SettingsFragment } from "building-types/settings/settingsFragment";
import { ineffectualSettingsFieldsSet } from "../ineffectualSettingsFields";
import type { FlattenSettingsSetOptions } from "./flattenSettingsSets";
import { flattenSettingsSets } from "./flattenSettingsSets";

/**
 * An object matching the shape of the home settings object but storing settings ids
 * instead of settings data. This is useful for determining which settings are active
 * and which are inherited.
 */

export interface SettingsFragmentProvenance {
  [key: string]: SettingsFieldProvenance;
}

export type SettingsFieldProvenance = string | string[] | SettingsFragmentProvenance | SettingsFragmentProvenance[];

export function deriveHomeSettingsProvenance(props: FlattenSettingsSetOptions): SettingsFragmentProvenance {
  const { settings, settingsSets, optionChoices, optionSets, entityDisabledByGroup } = props;
  const settingsTableAsProvenance = {};
  for (const settingsId in settings) {
    settingsTableAsProvenance[settingsId] = convertSettingsFragmentToProvenance(settings[settingsId]);
  }

  const { mergedSettings: settingsProvenance } = flattenSettingsSets(
    {} as any,
    {
      settingsSets,
      optionChoices,
      settings: settingsTableAsProvenance,
      optionSets,
      entityDisabledByGroup,
    } as any,
  ) as any;

  return settingsProvenance;
}

function convertSettingsFragmentToProvenance(obj: SettingsFragment): SettingsFragmentProvenance {
  const result = {};
  for (const key in obj) {
    // preserve these fields because they are important for merging the settings
    // objects later
    if (ineffectualSettingsFieldsSet.has(key as keyof SettingsFragment)) {
      result[key] = obj[key];
      continue;
    }

    result[key] = settingsFragmentFieldProvenance(obj.id, obj[key]);
  }
  return result;
}

function settingsFragmentFieldProvenance(settingsId: string, obj: any) {
  switch (typeof obj) {
    case "number":
    case "string":
    case "boolean": {
      return settingsId;
    }
    case "object": {
      // we deliberately don't recurse on fields of arrays and objects as that just gets
      // too complicated. thus, the provenance of an array or object is an array or object of
      // settings ids respectively
      if (obj === null) {
        return null;
      } else if (Array.isArray(obj)) {
        return obj.map(() => settingsId);
      } else {
        const result = {};
        for (const key in obj) {
          result[key] = settingsId;
        }
        return result;
      }
    }
    default:
      return undefined;
  }
}
